import { useState, useRef, useEffect } from "react";
import {
    Form,
    Link,
    useNavigate,
    useRouteLoaderData,
    useSearchParams,
} from "react-router-dom";
import Cleave from "cleave.js/react";
import { states, cityStates } from "src/components/common/cityState";
import { Lead, sourceOptions, statusOptions } from "./types";
import { AsyncFormSelect, SelectOption } from "src/components/common/AsyncSelect";
import { myAxios } from "../common/functions";
export default function LeadForm({
    keyword
}: {
    keyword: "Add" | "Edit";
}) {
    const loaderOrg =
    useRouteLoaderData("lead") ?? {
        organizationName: "", 
        organizationId: null, 
        firstName: "", 
        middleName: "",
        lastName: "",
        email: "", 
        role: "", 
        mobile: "",
        businessPhone: "",
        extension: "",
        fax: "",
        street: "",
        city: "",
        state: "",
        zipCode: "",
        id: "",
        sent: 0,
        realSent: 0,
        background: "",  

        webiste: "", 
        industry: "",
        source: "",
        status: "",
        userResponsible1: null,
        userResponsible2: null,
        referredBy: null,
    };
    // emailValid
    const [formValues, setFormValues] = useState<Lead>(loaderOrg);
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormValues((prev)=> ({
            ...prev,
            [name]: value
        }));
    };
    const formRef = useRef(null);
    const organizationIdParam = useSearchParams()[0].get("organizationId");
    const navigate = useNavigate();
    const handleCopyAddress = async () => {
        if(!formValues.organizationId){
            alert("No organization linked or organization not found.");
            return;
        }
        const formData = new FormData();
        formData.append("id", String(formValues.organizationId));
        const res = await myAxios({ fileName: "leads/getOrganizationAddress.php", formData });
        if(res && res.data){
            const { street, city, state, zipCode } = res.data;
            setFormValues((formValues) => ({...formValues, street, city, state, zipCode}));
        }
    }
        const handleCopyOverseaAddress = async () => {
        if(!formValues.organizationId){
            alert("No organization linked or organization not found.");
            return;
        }
        const formData = new FormData();
        formData.append("id", String(formValues.organizationId));
        const res = await myAxios({ fileName: "leads/getOrganizationOverseaAddress.php", formData });
        if(res && res.data){
            setFormValues((formValues) => ({...formValues, overseaAddress: res.data}));
        }
    }
    useEffect(() => {
        const controller = new AbortController();
        let isMouted = true;
        const getData = async () => {
            if(keyword === "Add" && organizationIdParam){
                const formData = new FormData();
                formData.append("id", organizationIdParam ?? "");
                const res = await myAxios({ fileName: "leads/getOrganizationName.php", formData, signal: controller.signal });
                if(!isMouted){
                    return;
                }
                if(!res || !res.data){
                     navigate(`/Leads/Add`, { replace: true });
                     return;
                }
                setFormValues((formValues) => ({...formValues, organizationName: res.data.name, organizationId: Number(organizationIdParam)}));
            }
        }
        getData();
        return () => {
            isMouted = false;
            controller.abort();
        };
    }, [keyword, organizationIdParam, navigate]);
    return (
        <Form method="post" ref = {formRef}>
            <input type="hidden" name="action" value={keyword} />
            <input type="hidden" name="id" value={formValues.id ?? ""} />
            <div className="cssSeperateWhenWide ps-3 mb-2">
                <div className="flex-grow-1 me-3">
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0 required-field" htmlFor="firstName">
                            First Name:
                        </label>
                        <input
                            required
                            className="form-control flex-grow-1 ms-3"
                            id="firstName"
                            name="firstName"
                            value={formValues.firstName}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="middleName">
                            Middle Name:
                        </label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="middleName"
                            name="middleName"
                            value={formValues.middleName}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="lastName">
                            Last Name:
                        </label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="lastName"
                            name="lastName"
                            value={formValues.lastName}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0 required-field" htmlFor="organizationId">
                            Organization Name:
                        </label>
                        <AsyncFormSelect
                            className="flex-grow-1 ms-3"
                            placeholder = "Search organization…"
                            // required = {!!field.is_required} --- IGNORE ---
                            name = {"organizationId"}
                            inputId = {"organizationId"}
                            readOnly = {!!organizationIdParam}
                            fileName = "common/searchOrganization.php"
                            value = {formValues.organizationId !== null ? {label: formValues.organizationName!, value: String(formValues.organizationId ?? "")} : null}
                            onChange = {next => 
                                setFormValues({
                                    ...formValues,
                                    organizationId: !next ? null : Number((next as SelectOption)?.value),
                                    organizationName: (next as SelectOption)?.label ?? null
                                })
                            }
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold mb-0" htmlFor="role">Role:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="role"
                            name="role"
                            value={formValues.role}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="businessPhone">Business Phone:</label>
                        <Cleave
                            className="form-control flex-grow-1 ms-3"
                            id="businessPhone"
                            name="businessPhone"
                            value={formValues.businessPhone}
                            onChange={(e) =>
                                setFormValues({
                                    ...formValues,
                                    businessPhone: e.target.value
                                })
                            }
                            options={{
                                blocks: [3, 3, 4],
                                delimiter: "-",
                                numericOnly: true
                            }}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold mb-0" htmlFor="extension">Extension:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="extension"
                            name="extension"
                            value={formValues.extension}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold mb-0" htmlFor="fax">Fax:</label>
                        <Cleave
                            className="form-control flex-grow-1 ms-3"
                            id="fax"
                            name="fax"
                            value={formValues.fax}
                            onChange={(e) =>
                                setFormValues({
                                    ...formValues,
                                    fax: e.target.value
                                })
                            }
                            options={{
                                blocks: [3, 3, 4],
                                delimiter: "-",
                                numericOnly: true
                            }}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="mobile">Mobile Number:</label>
                        <Cleave
                            className="form-control flex-grow-1 ms-3"
                            id="mobile"
                            name="mobile"
                            value={formValues.mobile}
                            onChange={(e) =>
                                setFormValues({
                                    ...formValues,
                                    mobile: e.target.value
                                })
                            }
                            options={{
                                blocks: [3, 3, 4],
                                delimiter: "-",
                                numericOnly: true
                            }}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="email">
                            Email:
                        </label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id = "email"
                            name="email"
                            value={formValues.email}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <div className="flex-grow-1 me-3">
                    <h4 className = "fw-bold text-center mt-2">Address</h4>
                    <button type="button" className="btn btn-warning" onClick={handleCopyAddress}>Copy Address from Organization</button>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold mb-0" htmlFor="street">Street:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="street"
                            name="street"
                            value={formValues.street}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="city">City:</label>
                        <select
                            className="form-control flex-grow-1 ms-3"
                            id="city"
                            name="city"
                            value={formValues.city}
                            onChange={handleChange}
                        >
                            <option value="">Select</option>
                            {formValues.state &&
                                cityStates[formValues.state as keyof typeof cityStates]?.map(
                                    (s, i) => <option key={i}>{s}</option>
                                )}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="state">State:</label>
                        <select
                            className = "form-control flex-grow-1 ms-3"
                            id="state"
                            name = "state"
                            value = {formValues.state}
                            onChange = {handleChange}
                        >
                            <option value="">Select</option>
                            {states.map((s, i) => (
                                <option key={i}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="zipCode">Zip Code:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="zipCode"
                            name="zipCode"
                            value={formValues.zipCode}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="mt-2">
                        <button type="button" className="btn btn-warning" onClick={handleCopyOverseaAddress}>Copy Oversea Address from Organization</button>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="zipCode">Oversea Address:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="overseaAddress"
                            name="overseaAddress"
                            value={formValues.overseaAddress}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="website">
                            Webiste:
                        </label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id = "website"
                            name="website"
                            value={formValues.website}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="industry">
                            Industry:
                        </label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id = "industry"
                            name="industry"
                            value={formValues.industry}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="source">
                            Source:
                        </label>
                        <select
                            className="form-control flex-grow-1 ms-3"
                            id = "source"
                            name="source"
                            value={formValues.source}
                            onChange={handleChange}
                        >
                            <option value="">Select</option>
                            {sourceOptions.map(s => <option key = {s}>{s}</option>)}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="status">
                            Status:
                        </label>
                        <select
                            className="form-control flex-grow-1 ms-3"
                            id = "status"
                            name="status"
                            value={formValues.status}
                            onChange={handleChange}
                        >
                            <option value="">Select</option>
                            {statusOptions.map(s => <option key = {s}>{s}</option>)}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="userResponsible1">
                            User Responsible 1:
                        </label>
                        <AsyncFormSelect
                            className="flex-grow-1 ms-3"
                            placeholder = "Search user…"
                            name = {"userResponsible1"}
                            inputId = {"userResponsible1"}
                            fileName = "common/searchUser.php"
                            value = {formValues.userResponsible1 !== null ? {label: formValues.userResponsible1Name, value: String(formValues.userResponsible1)} : null}
                            onChange = {next => 
                                setFormValues({
                                    ...formValues,
                                    userResponsible1: (next as SelectOption)?.value ?? null,
                                    userResponsible1Name: (next as SelectOption)?.label ?? null
                                })
                            }
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="userResponsible2">
                            User Responsible 2:
                        </label>
                        <AsyncFormSelect
                            className="flex-grow-1 ms-3"
                            placeholder = "Search user…"
                            name = {"userResponsible2"}
                            inputId = {"userResponsible2"}
                            fileName = "common/searchUser.php"
                            value = {formValues.userResponsible2 !== null ? {label: formValues.userResponsible2Name, value: String(formValues.userResponsible2)} : null}
                            onChange = {next => 
                                setFormValues({
                                    ...formValues,
                                    userResponsible2: (next as SelectOption)?.value ?? null,
                                    userResponsible2Name: (next as SelectOption)?.label ?? null
                                })
                            }
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap mb-0" htmlFor="referredBy">
                            Referred By:
                        </label>
                        <AsyncFormSelect
                            className="flex-grow-1 ms-3"
                            placeholder = "Search user…"
                            name = {"referredBy"}
                            inputId = {"referredBy"}
                            fileName = "common/searchUser.php"
                            value = {formValues.referredBy !== null ? {label: formValues.referredByName!, value: String(formValues.referredBy)} : null}
                            onChange = {next => 
                                setFormValues({
                                    ...formValues,
                                    referredBy: (next as SelectOption)?.value ?? null,
                                    referredByName: (next as SelectOption)?.label ?? null
                                })
                            }
                        />
                    </div>
                </div>
                <div className="d-flex flex-column align-items-start mt-2">
                    <label className="fw-bold mb-0" htmlFor="background">Background:</label>
                    <textarea
                        className="form-control w-100 flex-grow-1"
                        id="background"
                        name="background"
                        value={formValues.background}
                        onChange={handleChange}
                    />
                </div>
            </div>
            <div className="my-3">
                <button
                    type="submit"
                    className="btn btn-primary"
                >
                    {keyword}
                </button>
                <Link
                    to={organizationIdParam?`/Organizations/${organizationIdParam}`:".."}
                    className="btn btn-outline-secondary float-end"
                >
                    Back
                </Link>
            </div>
        </Form>
    );
}
